import {inject, Injectable} from '@angular/core';
import {firstValueFrom, Observable} from 'rxjs';
import {UserGroupStore} from '../stores/user-group.store';
import {UserGroup} from '../api/models/user-group';
import {map, take} from 'rxjs/operators';
import {UserGroupMember} from '../api/models/user-group-member';
import {VaultMember} from '../api/models/vault-member';
import {EntityQueryService} from '../modules/akita-mimic/services/entity-query.service';
import {UserGroupState} from '../modules/akita-mimic/models/store-states/user-group-state.model';
import {SortOrder} from '../modules/akita-mimic/enums/sort-order.enum';

@Injectable({ providedIn: 'root' })
export class UserGroupQuery extends EntityQueryService<UserGroupState, UserGroup> {
    public userGroups$: Observable<Array<UserGroup>> = this.selectAll();
    public activeUserGroup$: Observable<UserGroup | undefined> = this.selectFirstActive();
    public userGroupMember$: Observable<Array<UserGroupMember>> = this.select('member')
        .pipe(
            map(list => {
                    const listCopy = JSON.parse(JSON.stringify(list));
                    return listCopy.sort((a: UserGroupMember, b: UserGroupMember) => {
                        return a.userFullName.localeCompare(b.userFullName);
                    });
                }
            )
        );
    public isLoading$: Observable<boolean> = this.selectLoading();

    public constructor() {
        const store = inject(UserGroupStore);
        super(store, {
            sortBy: 'name',
            sortByOrder: SortOrder.ASC
        });
    }

    public async getPaginatedGroups(offset: number, limit: number): Promise<Array<UserGroup>> {
        const storeGroups = await firstValueFrom(this.userGroups$.pipe(take(1)));
        let groups: Array<UserGroup> = [];
        if (storeGroups) {
            groups = storeGroups.slice(offset, offset + limit);
        }
        return groups;
    }

    public getUserGroupMembers(): Array<UserGroupMember | VaultMember> {
        return this.getValue().member;
    }

    public async getUserGroupMembersPaginated(offset: number, limit: number): Promise<Array<UserGroupMember>> {
        const memberList = await firstValueFrom(this.userGroupMember$);
        return memberList.slice(offset, offset + limit);
    }

    public getUserGroupById(userGroupId: string): UserGroup | undefined {
        return this.getEntity(userGroupId);
    }
}
